import { GetItemsProps } from 'medere-ui-framework';
import { axiosInstance } from '../App';
import { SECURED_API_BASE } from './apiConfig';
import { AsyncExportJobDTO } from '../dto/AsyncExportDTO';
import {
    DrugItemComboDTO,
    DrugTagCostDetailCategory,
    DrugTagCostSort,
    DrugTagPrescriptionCostDetailDTO,
    DrugTagPrescriptionCostCountDTO,
    DrugTagPrescriptionCostRowDTO,
    DrugTagPrescriptionReportFilter,
    DrugTagReportExportFormat,
    DrugTagPrescriptionReportIndicatorsDTO,
    DrugTagPrescriptionReportItemDTO,
} from '../dto/DrugTagPrescriptionReportDTO';

const BASE = `${SECURED_API_BASE}/drugtagprescriptionreport`;

/** Una página del reporte, para la tabla con scroll infinito. */
export function getDrugTagPrescriptionReport(filter: DrugTagPrescriptionReportFilter, offset: number,
                                             pageSize: number): Promise<DrugTagPrescriptionReportItemDTO[]> {
    return axiosInstance.get(`${BASE}/getDetail`, { params: { ...filter, offset, pageSize } }).then(r => r.data ?? []);
}

/** Indicadores de prescripto contra aplicado y filas de la grilla; se piden una vez por consulta. */
export function getDrugTagPrescriptionReportIndicators(
    filter: DrugTagPrescriptionReportFilter,
): Promise<DrugTagPrescriptionReportIndicatorsDTO> {
    return axiosInstance.get(`${BASE}/getIndicators`, { params: filter }).then(r => r.data);
}

/**
 * Arranca en el backend el Excel con todas las dosis que cumplen los filtros o, con `pdf`, un PDF con las primeras
 * 2.000 (ver useAsyncExport). `filtersText` es el resumen de filtros que lleva el PDF arriba.
 */
export function startDrugTagPrescriptionReportExport(filter: DrugTagPrescriptionReportFilter,
                                                     format: DrugTagReportExportFormat = 'xlsx',
                                                     filtersText?: string): Promise<AsyncExportJobDTO> {
    return axiosInstance.post(`${BASE}/export`, null, {
        params: { ...filter, format, filtersText: format === 'pdf' ? filtersText : undefined },
    }).then(r => r.data);
}

/** Una página de la tab Costo, en el orden pedido. */
export function getDrugTagPrescriptionCost(filter: DrugTagPrescriptionReportFilter, sort: DrugTagCostSort,
                                           offset: number, pageSize: number): Promise<DrugTagPrescriptionCostRowDTO[]> {
    return axiosInstance.get(`${BASE}/getCost`, { params: { ...filter, ...sort, offset, pageSize } })
        .then(r => r.data ?? []);
}

/**
 * Una página del detalle de una fila de Costo: sus dosis por fecha programada, de una categoría o de todas. Sin
 * presentación, la fila de lo aplicado sin nada preparado.
 */
export function getDrugTagPrescriptionCostDetail(filter: DrugTagPrescriptionReportFilter,
                                                 row: { healthEntity: number; drugItem: number;
                                                        presentation: number | null },
                                                 category: DrugTagCostDetailCategory, offset: number,
                                                 pageSize: number): Promise<DrugTagPrescriptionCostDetailDTO[]> {
    return axiosInstance.get(`${BASE}/getCostDetail`, {
        params: {
            ...filter,
            detailHealthEntity: row.healthEntity,
            detailDrugItem: row.drugItem,
            detailPresentation: row.presentation ?? undefined,
            detailCategory: category,
            offset,
            pageSize,
        },
    }).then(r => r.data ?? []);
}

/** Filas de la tab Costo y dosis sin costo cargado; se pide una vez por consulta. */
export function getDrugTagPrescriptionCostCount(
    filter: DrugTagPrescriptionReportFilter,
): Promise<DrugTagPrescriptionCostCountDTO> {
    return axiosInstance.get(`${BASE}/getCostCount`, { params: filter }).then(r => r.data);
}

/** Arranca en el backend el Excel de la tab Costo, en el orden de la grilla (ver useAsyncExport). */
export function startDrugTagPrescriptionCostExport(filter: DrugTagPrescriptionReportFilter,
                                                   sort: DrugTagCostSort): Promise<AsyncExportJobDTO> {
    return axiosInstance.post(`${BASE}/exportCost`, null, { params: { ...filter, ...sort } }).then(r => r.data);
}

/**
 * Medicación para el Combobox del filtro (medicamentos, soluciones parenterales y suplementos), por nombre. Con
 * etiquetas, solo la que tiene alguna de ellas.
 */
export function getDrugItemsForCombo({ filterValue, offSet, pageSize, params }: GetItemsProps<{ drugTagIds: number[] }>):
    Promise<DrugItemComboDTO[]> {
    const drugTagIds = params?.drugTagIds ?? [];
    return axiosInstance.get(`${BASE}/getDrugItemsForCombo`, {
        params: {
            inputText: filterValue ?? '',
            offSet: offSet ?? 0,
            pageSize: pageSize ?? 20,
            drugTagIds: drugTagIds.length > 0 ? drugTagIds.join(',') : undefined,
        },
    }).then(r => r.data ?? []);
}
